// The global scope (R-U7, R-E1): one chosen record of the app's scope type filters scoped sections and prefills new records; it is
// chosen on the plate at the bottom of the left column.

import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { useSetting } from '../settings/use-setting.ts';
import { accountToday, bindScope, recordTitle, recordWords, type FieldDef, type Filter, type Id, type RecordObj, type RecordWords } from '@confdeck/core-shared';
import { PickList } from '../ui/PickList.tsx';
import { defaultScope, eventDates, scopeOrder } from './scope-dates.ts';
import { useMeta, useRecord, useSync, useTypesTick } from '../sync/react.tsx';
import { Icon } from '../ui/icons.tsx';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/primitives.tsx';
import s from './shell.module.css';

const listeners = new Set<() => void>();
const key = () => `confdeck:scope:${location.host}`;
function read(): string | null { try { return localStorage.getItem(key()); } catch { return null; } }
let current: string | null = typeof window === 'undefined' ? null : read();
/** Writes the choice to the person's setting on the server (L11): another computer of the person opens the same event. */
let remember: ((v: string | null) => void) | null = null;
function set(v: string | null, fromServer = false) {
  if (v === current) return;
  current = v;
  try { if (v) localStorage.setItem(key(), v); else localStorage.removeItem(key()); } catch { /* the server keeps it too */ }
  if (!fromServer) remember?.(v);
  for (const l of listeners) l();
}
const choose = (v: Id | null) => set(v);

/**
 * Joins the scope choice to the person's setting «ui.scope»: a browser without its own choice takes the server's, and every new
 * choice is written there. Mounted once by the shell.
 */
export function useScopeSync(): void {
  const client = useSync();
  const saved = useSetting('ui.scope');
  const me = client.header?.userId;
  useEffect(() => {
    remember = me ? (v) => { void client.run('setting.set', { key: 'ui.scope', scope: 'user', targetId: me, value: v ?? '' }); } : null;
    return () => { remember = null; };
  }, [client, me]);
  useEffect(() => { if (typeof saved === 'string' && saved && saved !== current && !read()) set(saved, true); }, [saved]);
}

/** The chosen scope record id; falls back to the newest record of the scope type. */
export function useScope(): [Id | null, (v: Id | null) => void] {
  const client = useSync();
  const meta = useMeta();
  const typeId = client.header?.scope?.typeId;
  useTypesTick(typeId ? [typeId] : []);
  const v = useSyncExternalStore((cb) => { listeners.add(cb); return () => listeners.delete(cb); }, () => current);
  if (!typeId) return [null, choose];
  if (v && client.store.record(v)) return [v, choose];
  // Default: the nearest upcoming record by the scope's day, else the latest one; otherwise the newest created. «Today» is the account's, as on the plate.
  const scope = client.header!.scope!;
  const recs = [...client.store.recordIds(typeId)].map((rid) => client.store.record(rid)).filter((r): r is RecordObj => !!r);
  return [defaultScope(recs, scope, accountToday(meta)), choose];
}

/**
 * The words of the chosen scope record (R-E9, TypeDef.wording): other names of the sections it narrows and the screens it has not —
 * the tickets of a product without a date are «Доступы», a broadcast has no entrance desk.
 */
export function useScopeWords(): RecordWords {
  const meta = useMeta();
  const [id] = useScope();
  return recordWords(meta, useRecord(id ?? undefined));
}

/** The relation field of a type that points at the scope type, if the type is scoped at all. */
export function scopeField(meta: ReturnType<typeof useMeta>, typeId: Id, scopeTypeId: Id | undefined): FieldDef | undefined {
  if (!scopeTypeId || typeId === scopeTypeId) return undefined;
  return meta.fields(typeId).find((f) => f.fieldType === 'relation' && meta.relationTarget(f)?.typeId === scopeTypeId && !meta.relationTarget(f)?.many);
}

/** The view filter narrowed to the chosen scope record: by the type's own link to it, and where the view names it (`$scope`). */
export function withScope(raw: Filter | null, field: FieldDef | undefined, scopeId: Id | null): Filter | null {
  const filter = bindScope(raw, scopeId);
  if (!field || !scopeId) return filter;
  const cond: Filter = { field: field.key, op: 'some', where: { field: 'id', op: 'in', value: [scopeId] } };
  return filter ? { and: [filter, cond] } : cond;
}

/**
 * The plate of the chosen event at the bottom of the left column (um/2026-10-30/04 п.2): one look in every section the event narrows —
 * its kind, name and dates; a click opens the choice. The only place the event is chosen. It stands in `ColumnFoot`, under the
 * screens fixed there (`FootScreen`).
 */
export function ScopePlate() {
  const client = useSync();
  const meta = useMeta();
  const scope = client.header?.scope;
  const [value, setValue] = useScope();
  const [open, setOpen] = useState(false);
  const options = useMemo(() => {
    if (!scope) return [];
    // R-E10: a flow of small events is chosen by date — the coming ones first, the nearest on top, then those without a date, then the past.
    const today = accountToday(meta);
    const GROUPS = ['Ближайшие', 'Без даты', 'Прошедшие'];
    const recs = [...client.store.recordIds(scope.typeId)].map((rid) => client.store.record(rid)!).filter((r) => r && !r.archivedAt);
    return scopeOrder(recs, scope, today)
      .map(({ r, g }) => ({ id: r.id, label: recordTitle(r, meta), ...(scope.dateField ? { group: GROUPS[g] } : {}), hint: eventDates(r.data[scope.dateField ?? ''], r.data[scope.endField ?? '']) || undefined }));
  }, [scope, client.store, meta, open]);
  if (!scope) return null;
  const rec = value ? client.store.record(value) : undefined;
  const dates = rec && scope.dateField ? eventDates(rec.data[scope.dateField], scope.endField ? rec.data[scope.endField] : null) : '';
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className={s.scopeBtn} data-scope-plate aria-label={`${scope.label}: ${rec ? recordTitle(rec, meta) : 'не выбрано'}. Выбрать`}>
          <Icon name="date" size={16} />
          <span className={s.scopeText}>
            <span className={s.scopeValue}>{rec ? recordTitle(rec, meta) : 'Не выбрано'}</span>
            <span className={s.scopeLabel}>{dates || scope.label}</span>
          </span>
          <Icon name="chevronsUpDown" size={14} />
        </button>
      </PopoverTrigger>
      <PopoverContent width={360} side="top">
        <PickList options={options} selected={value ? [value] : []} onPick={(id) => setValue(id)} onDone={() => setOpen(false)} placeholder={`Найти: ${scope.label.toLowerCase()}`} />
      </PopoverContent>
    </Popover>
  );
}

/** The bottom of the left column: one slot for the event's plate or a section's own action (R-U23), the same paddings and line. */
export function ColumnFoot({ children }: { children: React.ReactNode }) {
  return <div className={s.scope}>{children}</div>;
}

/**
 * A screen fixed at the bottom of the column above the plate of the event (um/2026-10-31/03: «Вход на событие»): the plate's look —
 * icon, name, a line under it — but a plain button that opens the screen.
 */
export function FootScreen({ icon, label, hint, on, onClick }: { icon: string; label: string; hint?: string; on: boolean; onClick: () => void }) {
  return (
    <button type="button" className={s.scopeBtn} data-foot-screen={label} aria-current={on ? 'page' : undefined} onClick={onClick}>
      <Icon name={icon} size={16} />
      <span className={s.scopeText}>
        <span className={s.scopeValue}>{label}</span>
        {hint && <span className={s.scopeLabel}>{hint}</span>}
      </span>
      <Icon name="chevronRight" size={14} />
    </button>
  );
}
